import React from 'react';
import {
  GlobeAltIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  ComputerDesktopIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { SolutionPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const painPoints = [
  {
    icon: DocumentCheckIcon,
    title: 'No Local Entities',
    description: "Can't legally import into target markets without IOR setup in each country",
  },
  {
    icon: UserGroupIcon,
    title: 'Limited Logistics Team',
    description: "You need to ship globally but don't have the headcount to manage it",
  },
  {
    icon: ClockIcon,
    title: 'Speed to Market',
    description: 'Customers expect fast, reliable delivery worldwide from day one',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'Asset Lifecycle Complexity',
    description: 'Managing repairs, returns, and redeployment across borders adds operational burden',
  },
];

const solutions = [
  {
    icon: DocumentCheckIcon,
    title: 'IOR/EOR & Compliance',
    points: [
      'Ship to 140+ countries without local entities',
      'Full duties and taxes management',
      'Regulatory compliance handled end-to-end',
      'Fast market entry in new regions',
    ],
  },
  {
    icon: BuildingOffice2Icon,
    title: 'Global Warehousing',
    points: [
      'Regional hubs in Singapore, Dubai, and partner facilities',
      'Fast fulfilment from strategically positioned inventory',
      'Centralised stock visibility and management',
      'Scalable capacity as you grow',
    ],
  },
  {
    icon: ComputerDesktopIcon,
    title: 'Device Staging & Fulfilment',
    points: [
      'Pre-configured and tested before shipping',
      'Software imaging and labelling',
      'Installation-ready delivery to end users',
      'Quality validation at every stage',
    ],
  },
  {
    icon: ArrowPathIcon,
    title: 'Reverse Logistics',
    points: [
      'Structured returns processing globally',
      'Certified data wiping (NIST 800-88)',
      'Asset recovery and refurbishment',
      'Full audit trails for compliance',
    ],
  },
];

const whyChooseCards = [
  {
    icon: GlobeAltIcon,
    title: 'Scale Without Infrastructure',
    description: 'Access 140+ countries through one partner, without building logistics teams or local entities.',
    accent: 'bg-blue-500',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Enterprise-Grade Execution',
    description: 'Same precision and compliance as large operators, scaled to your needs and budget.',
    accent: 'bg-cyan-500',
  },
  {
    icon: CodeBracketIcon,
    title: 'API-First Integration',
    description: 'Connect your platform directly to our WMS for automated ordering, tracking, and reporting.',
    accent: 'bg-indigo-500',
  },
];

const TechStartups: React.FC = () => (
  <SolutionPageTemplate
    eyebrow="Tech Startups & Scale-ups"
    title="Global ICT Logistics Without the Overhead"
    highlight="Without the Overhead"
    description="Scale your hardware operations internationally without building logistics infrastructure. TrueLog handles compliance, fulfilment, and delivery so you can focus on growth."
    heroIcon={RocketLaunchIcon}
    heroAccentIcon={GlobeAltIcon}
    problemHeading="The challenges you face"
    problemDescription="Growing globally is exciting, but hardware logistics can become a bottleneck fast without the right partner."
    painPoints={painPoints}
    solutionsIntro="TrueLog gives early-stage and scaling teams the operational backbone needed to move hardware globally with confidence."
    solutions={solutions}
    whyChooseCards={whyChooseCards}
    ctaTitle="Ready to scale your hardware operations globally?"
    ctaDescription="Let TrueLog handle the complexity of cross-border logistics so your team can keep moving fast."
  />
);

export default TechStartups;
